import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ShoppingCart, 
  Heart, 
  Minus, 
  Plus, 
  Truck, 
  Shield, 
  RotateCcw,
  Check,
  ChevronLeft,
  Star,
  Sparkles
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import IntensityIndicator from '../components/IntensityIndicator';
import OlfactoryPyramid from '../components/OlfactoryPyramid';
import ProductReviewsCarousel from '../components/ProductReviewsCarousel';
import ProductTrustBadges from '../components/ProductTrustBadges';
import { trackViewContent, trackAddToWishlist } from '../utils/fbPixel';
import { productPath } from '../utils/helpers';
import { ProductStamp, SoldOutRibbon } from '../components/design-system';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Track product view for internal analytics
const trackProductView = async (productId, sessionId) => {
  try {
    await fetch(`${API_URL}/api/analytics/view`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        product_id: productId,
        session_id: sessionId || localStorage.getItem('cart_session_id') || ''
      })
    });
  } catch (e) {
    // Silent fail - don't break UX for analytics
  }
};

// Helper functions for gender-based brand colors
const getAccentColor = (gender) => gender === 'Mujer' ? 'magenta' : 'cobalt';
const getTextColorClass = (gender) => gender === 'Mujer' ? 'text-brand-magenta' : 'text-brand-cobalt';
const getBorderColorClass = (gender) => gender === 'Mujer' ? 'border-brand-magenta' : 'border-brand-cobalt';
const getBgColorClass = (gender) => gender === 'Mujer' ? 'bg-brand-magenta' : 'bg-brand-cobalt';
const getHoverShadowClass = (gender) => gender === 'Mujer' ? 'magenta-accent' : '';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  
  const [product, setProduct] = useState(null);
  const [similarProducts, setSimilarProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState(100); // 100ml or 35ml
  const [isFavorite, setIsFavorite] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    loadProduct();
    loadSimilarProducts();
  }, [id]);

  const loadProduct = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/products/${id}`);
      if (res.ok) {
        const data = await res.json();
        setProduct(data);
        // Links viejos por id → URL corta con el código del producto
        if (data.product_code && id !== data.product_code) {
          navigate(productPath(data), { replace: true });
        }
        // Track ViewContent for Facebook Pixel
        trackViewContent(data);
        // Track for internal analytics
        trackProductView(data.id);
      } else {
        navigate('/products');
      }
    } catch (error) {
      console.error('Error loading product:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSimilarProducts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/products/${id}/similar?limit=4`);
      if (res.ok) {
        const data = await res.json();
        setSimilarProducts(Array.isArray(data) ? data : data.products || []);
      }
    } catch (error) {
      console.error('Error loading similar products:', error);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(price);
  };

  const handleAddToCart = () => {
    const currentStock = selectedSize === 40 ? (product.stock_40ml || 0) : product.stock;
    
    if (currentStock === 0) {
      toast.error(`Producto agotado en ${selectedSize === 40 ? 35 : selectedSize}ml`);
      return;
    }
    
    const currentPrice = selectedSize === 40 ? (product.price_40ml || 250) : product.price;
    
    // Create product with selected size info
    const productWithSize = {
      ...product,
      selectedSize: selectedSize,
      currentPrice: currentPrice,
      currentStock: currentStock
    };
    
    for (let i = 0; i < quantity; i++) {
      addItem(productWithSize, selectedSize);
    }
    
    setAddedToCart(true);
    toast.success(`Agregado al carrito (${selectedSize === 40 ? 35 : selectedSize}ml)`);
    
    setTimeout(() => setAddedToCart(false), 2000);
  };

  const getAllImages = () => {
    const images = [];
    if (product?.main_image) images.push(product.main_image);
    if (product?.gallery_images?.length) {
      images.push(...product.gallery_images);
    }
    if (images.length === 0) {
      images.push('https://images.unsplash.com/photo-1594035910387-fea47794261f?w=800');
    }
    return images;
  };

  const getGenderLabel = (gender) => {
    const labels = { men: 'Hombre', women: 'Mujer', unisex: 'Unisex' };
    return labels[gender] || gender;
  };

  const getFragranceFamilyLabel = (family) => {
    const labels = { woody: 'Amaderado', citrus: 'Cítrico', floral: 'Floral', oriental: 'Oriental', fresh: 'Fresco', spicy: 'Especiado' };
    return labels[family] || family;
  };

  const getIntensityLabel = (intensity) => {
    const labels = { light: 'Ligera', moderate: 'Moderada', strong: 'Fuerte' };
    return labels[intensity] || intensity;
  };

  // Loading skeleton con brand theme
  if (loading) {
    return (
      <div className="pb-24 bg-brand-bone min-h-screen">
        <div className="max-w-7xl mx-auto px-4 md:px-12">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            {/* Image skeleton */}
            <div className="animate-pulse">
              <div className="aspect-square bg-brand-cobalt/10 rounded-lg"></div>
              <div className="flex gap-2 mt-4">
                {[1,2,3].map(i => (
                  <div key={i} className="w-20 h-20 bg-brand-cobalt/10 rounded"></div>
                ))}
              </div>
            </div>
            {/* Info skeleton */}
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-brand-cobalt/10 rounded w-1/4"></div>
              <div className="h-8 bg-brand-cobalt/10 rounded w-3/4"></div>
              <div className="h-10 bg-brand-cobalt/10 rounded w-1/3"></div>
              <div className="h-24 bg-brand-cobalt/10 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  const images = getAllImages();

  return (
    <div className="pb-24 bg-brand-bone min-h-screen" data-testid="product-detail-page">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 md:px-12 py-4">
        <nav className="flex items-center gap-2 text-sm">
          <button
            onClick={() => navigate(-1)}
            className={`flex items-center gap-1 ${getTextColorClass(product?.gender)} hover:opacity-80 transition-colors`}
          >
            <ChevronLeft size={16} />
            <span>Volver</span>
          </button>
          <span className="text-brand-black/30">/</span>
          <span className="text-brand-black/50 truncate">{product.inspired_by_brand}</span>
        </nav>
      </div>

      {/* Main Content - 2 Columns */}
      <div className="max-w-7xl mx-auto px-4 md:px-12">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 lg:gap-16">
          
          {/* LEFT COLUMN - Images */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-4"
          >
            {/* Main Image */}
            <div className={`relative aspect-square brand-card hybrid-glow rounded-lg overflow-hidden group ${getHoverShadowClass(product?.gender)}`}>
              <img
                src={images[selectedImage]}
                alt={product.name}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <ProductStamp size="lg" />

              {/* Cinta "Agotado" igual que en el catálogo; reemplaza a los demás badges */}
              {product.stock === 0 ? (
                <SoldOutRibbon />
              ) : (
              <div className="absolute top-4 left-4 flex flex-col gap-2">
                {product.popularity?.includes('Nuevo Lanzamiento') && (
                  <span className="text-xs px-3 py-1 bg-brand-cobalt text-white font-medium uppercase tracking-wider">
                    Nuevo
                  </span>
                )}
                {product.popularity?.includes('Más vendidos') && (
                  <span className="text-xs px-3 py-1 bg-brand-magenta text-white font-medium uppercase tracking-wider">
                    Más Vendido
                  </span>
                )}
                {product.perfume_type === 'niche' && (
                  <span className="text-xs px-3 py-1 bg-brand-petroleum text-white font-medium uppercase tracking-wider flex items-center gap-1">
                    <Sparkles size={12} /> Nicho
                  </span>
                )}
              </div>
              )}

              {/* Favorite button */}
              <button
                onClick={() => setIsFavorite(!isFavorite)}
                className={`absolute top-4 right-4 p-3 rounded-full transition-all duration-300 ${
                  isFavorite 
                    ? 'bg-brand-magenta text-white' 
                    : 'bg-brand-bone/80 backdrop-blur-sm border border-brand-black/15 text-brand-black hover:border-brand-magenta'
                }`}
              >
                <Heart size={20} className={isFavorite ? 'fill-current' : ''} />
              </button>
            </div>

            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                {images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                      selectedImage === index 
                        ? `${getBorderColorClass(product?.gender)} shadow-luxury` 
                        : 'border-transparent hover:border-brand-black/20'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Short Description - Below image */}
            {product.short_description && (
              <div className={`brand-card hybrid-glow p-5 rounded-lg mt-4 ${getHoverShadowClass(product?.gender)}`}>
                <h3 className={`text-sm ${getTextColorClass(product?.gender)}/80 uppercase tracking-wider mb-3`}>Descripción</h3>
                <p className="text-brand-black/80 leading-relaxed text-sm">
                  {product.short_description}
                </p>
              </div>
            )}

            {/* Pirámide Olfativa - Componente visual */}
            <OlfactoryPyramid 
              notesTop={product.notes_top}
              notesHeart={product.notes_heart}
              notesBase={product.notes_base}
            />
          </motion.div>

          {/* RIGHT COLUMN - Product Info */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="space-y-4"
          >
            {/* Código - Negro, negritas, grande */}
            <p className="text-2xl md:text-3xl font-bold text-brand-black">
              {product.product_code}
            </p>

            {/* Inspirado en: - pequeño */}
            <p className="text-xs text-brand-black/50">Inspirado en:</p>

            {/* Nombre - Color según género (Hombre=cobalt, Mujer=magenta) */}
            <h1 className={`font-display text-3xl md:text-4xl lg:text-5xl leading-tight font-bold ${
              product.gender === 'Hombre' ? 'text-brand-cobalt' : 'text-brand-magenta'
            }`}>
              {product.inspired_by_name}
            </h1>

            {/* Marca */}
            <p className="text-xl md:text-2xl text-brand-black/70">
              {product.inspired_by_brand}
            </p>

            {/* Price */}
            <div className="flex items-baseline gap-3">
              <span className="font-display text-4xl md:text-5xl text-brand-black font-bold">
                {formatPrice(selectedSize === 40 ? (product.price_40ml || 250) : product.price)}
              </span>
              <span className="text-brand-black/50 text-lg">/ {selectedSize === 40 ? 35 : selectedSize}ml</span>
            </div>

            {/* Size Selector */}
            <div className="space-y-2">
              <p className={`text-xs ${getTextColorClass(product?.gender)}/60 uppercase tracking-wider`}>Selecciona el tamaño</p>
              <div className="flex gap-3">
                <button
                  onClick={() => { setSelectedSize(100); setQuantity(1); }}
                  disabled={product.stock === 0}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all duration-300 ${
                    product.stock === 0
                      ? 'border-brand-black/10 opacity-50 cursor-not-allowed'
                      : selectedSize === 100
                      ? `${getBorderColorClass(product?.gender)} ${getBgColorClass(product?.gender)}/10 shadow-lg ${product?.gender === 'Mujer' ? 'shadow-brand-magenta/30' : 'shadow-brand-cobalt/30'}`
                      : 'border-brand-black/20 hover:border-brand-black/30'
                  }`}
                >
                  <p className={`text-lg font-bold ${selectedSize === 100 && product.stock > 0 ? getTextColorClass(product?.gender) : 'text-brand-black'}`}>100ml</p>
                  <p className="text-brand-black/60 text-sm">{formatPrice(product.price)}</p>
                  {product.stock === 0 && (
                    <p className="text-xs mt-1 text-red-400">Agotado</p>
                  )}
                </button>
                <button
                  onClick={() => { setSelectedSize(40); setQuantity(1); }}
                  disabled={(product.stock_40ml || 0) === 0}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all duration-300 ${
                    (product.stock_40ml || 0) === 0
                      ? 'border-brand-black/10 opacity-50 cursor-not-allowed'
                      : selectedSize === 40
                      ? `${getBorderColorClass(product?.gender)} ${getBgColorClass(product?.gender)}/10 shadow-lg ${product?.gender === 'Mujer' ? 'shadow-brand-magenta/30' : 'shadow-brand-cobalt/30'}`
                      : 'border-brand-black/20 hover:border-brand-black/30'
                  }`}
                >
                  <p className={`text-lg font-bold ${selectedSize === 40 && (product.stock_40ml || 0) > 0 ? getTextColorClass(product?.gender) : 'text-brand-black'}`}>35ml</p>
                  <p className="text-brand-black/60 text-sm">{formatPrice(product.price_40ml || 250)}</p>
                  {(product.stock_40ml || 0) === 0 && (
                    <p className="text-xs mt-1 text-red-400">Agotado</p>
                  )}
                </button>
              </div>
            </div>

            {/* Star Rating - OCULTO temporalmente hasta tener suficientes reseñas */}
            {/*
            <div className="flex items-center gap-2">
              <div className="flex">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} size={18} className="text-amber-400 fill-amber-400" />
                ))}
              </div>
              <span className="text-brand-black/50 text-sm">(128 reseñas)</span>
            </div>
            */}

            {/* Divider */}
            <div className="h-px bg-gradient-to-r from-brand-cobalt/20 via-brand-magenta/20 to-transparent"></div>

            {/* Attributes Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className={`brand-card hybrid-glow p-4 rounded-lg ${getHoverShadowClass(product?.gender)}`}>
                <p className={`text-xs ${getTextColorClass(product?.gender)}/60 uppercase tracking-wider mb-1`}>Género</p>
                <p className="text-brand-black font-medium">{product.gender || 'Unisex'}</p>
              </div>
              <div className={`brand-card hybrid-glow p-4 rounded-lg ${getHoverShadowClass(product?.gender)}`}>
                <p className={`text-xs ${getTextColorClass(product?.gender)}/60 uppercase tracking-wider mb-1`}>Familia Olfativa</p>
                <p className="text-brand-black font-medium">
                  {product.olfactory_family && product.olfactory_family.length > 0 
                    ? product.olfactory_family.join(', ') 
                    : product.fragrance_family || '-'}
                </p>
              </div>
              <div className={`brand-card hybrid-glow p-4 rounded-lg ${getHoverShadowClass(product?.gender)}`}>
                <p className={`text-xs ${getTextColorClass(product?.gender)}/60 uppercase tracking-wider mb-1`}>Ocasión</p>
                <p className="text-brand-black font-medium">
                  {product.mood && product.mood.length > 0 
                    ? product.mood.join(', ') 
                    : '-'}
                </p>
              </div>
              <div className={`brand-card hybrid-glow p-4 rounded-lg ${getHoverShadowClass(product?.gender)}`}>
                <p className={`text-xs ${getTextColorClass(product?.gender)}/60 uppercase tracking-wider mb-1`}>Tamaño Seleccionado</p>
                <p className="text-brand-black font-medium">{selectedSize === 40 ? 35 : selectedSize} ml</p>
              </div>
            </div>

            {/* Intensity Indicator - Full Width */}
            <div className={`brand-card hybrid-glow p-4 rounded-lg ${getHoverShadowClass(product?.gender)}`}>
              <p className={`text-xs ${getTextColorClass(product?.gender)}/60 uppercase tracking-wider mb-2`}>Proyección</p>
              <IntensityIndicator 
                intensity={product.intensity || 'Moderada'} 
                size="md" 
                showLabels={true} 
              />
            </div>

            {/* Quantity Selector */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-brand-black/70">Cantidad:</span>
                <div className={`flex items-center border ${getBorderColorClass(product?.gender)}/30 rounded-lg overflow-hidden`}>
                  <button
                    onClick={() => setQuantity(q => Math.max(1, q - 1))}
                    className="p-3 text-brand-black hover:bg-brand-black/5 transition-colors"
                    disabled={quantity <= 1}
                  >
                    <Minus size={18} />
                  </button>
                  <span className="w-12 text-center text-brand-black font-medium">{quantity}</span>
                  <button
                    onClick={() => setQuantity(q => Math.min(selectedSize === 40 ? (product.stock_40ml || 0) : product.stock, q + 1))}
                    className="p-3 text-brand-black hover:bg-brand-black/5 transition-colors"
                    disabled={(selectedSize === 40 ? (product.stock_40ml || 0) : product.stock) <= quantity}
                  >
                    <Plus size={18} />
                  </button>
                </div>
              </div>
            </div>

            {/* Add to Cart Button */}
            <button
              onClick={handleAddToCart}
              disabled={(selectedSize === 40 ? (product.stock_40ml || 0) : product.stock) === 0}
              className={`w-full flex items-center justify-center gap-3 py-4 text-lg uppercase tracking-wider font-medium rounded-lg transition-all duration-300 ${
                (selectedSize === 40 ? (product.stock_40ml || 0) : product.stock) === 0
                  ? 'bg-brand-black/20 text-brand-black/40 cursor-not-allowed'
                  : addedToCart
                  ? 'bg-green-500 text-white shadow-lg'
                  : product?.gender === 'Mujer' ? 'brand-btn-magenta' : 'brand-btn'
              }`}
              data-testid="add-to-cart-btn"
            >
              {(selectedSize === 40 ? (product.stock_40ml || 0) : product.stock) === 0 ? (
                `Agotado (${selectedSize === 40 ? 35 : selectedSize}ml)`
              ) : addedToCart ? (
                <>
                  <Check size={22} />
                  ¡Agregado al carrito!
                </>
              ) : (
                <>
                  <ShoppingCart size={22} />
                  Agregar al Carrito
                </>
              )}
            </button>

            {/* Trust badges - Enhanced */}
            <ProductTrustBadges gender={product?.gender} />
          </motion.div>
        </div>

        {/* Product Reviews Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <ProductReviewsCarousel 
            productId={product?.id} 
            productName={product?.name} 
            gender={product?.gender}
          />
        </motion.section>

        {/* Similar Products */}
        {similarProducts && similarProducts.length > 0 && (
          <motion.section 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mt-16 md:mt-24"
          >
            <h2 className="font-display text-2xl md:text-3xl text-brand-black text-center mb-8">
              También te puede gustar
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {similarProducts.slice(0, 4).map((item) => (
                <Link 
                  key={item.id} 
                  to={productPath(item)} 
                  className="group"
                >
                  <div className={`brand-card hybrid-glow rounded-lg p-2 transition-all duration-300 ${item.gender === 'Mujer' ? 'magenta-accent' : ''}`}>
                    <div className="relative aspect-square bg-brand-black/5 rounded overflow-hidden mb-3">
                      <img
                        src={item.main_image || 'https://images.unsplash.com/photo-1594035910387-fea47794261f?w=400'}
                        alt={item.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <ProductStamp />
                    </div>
                    {/* Código */}
                    <p className="text-sm font-bold text-brand-black mb-0.5">
                      {item.product_code}
                    </p>
                    {/* Inspirado en: */}
                    <p className="text-[9px] text-brand-black/50">Inspirado en:</p>
                    {/* Nombre - Color según género */}
                    <p className={`text-base font-bold truncate transition-colors ${
                      item.gender === 'Hombre' 
                        ? 'text-brand-cobalt group-hover:text-brand-cobalt/80' 
                        : 'text-brand-magenta group-hover:text-brand-magenta/80'
                    }`}>
                      {item.inspired_by_name}
                    </p>
                    {/* Marca */}
                    <p className="text-sm text-brand-black/70 truncate">
                      {item.inspired_by_brand}
                    </p>
                    {/* Precio */}
                    <p className="text-sm text-brand-black font-medium mt-1">
                      {formatPrice(item.price)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </motion.section>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
